import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { data, portal, type Connection } from "@aawaz/api-client";
import {
  Badge, Button, Card, ChannelLogo, EmptyState, ErrorBanner, Field, Icon, Input, Meter, Modal, Select, SkeletonRows, fmtDate,
} from "@aawaz/ui";
import { useClients } from "../components/ItemPanel";
import { useSession } from "../session";

interface Target { id: string; connection_id: string; root_display_path: string; scope: string; client_org_ids?: string[] | null; status: string;
  backfill: { state?: string; done?: number; total?: number }; problems: number; updated_at: string }
interface Entry { id: string; item_id: string | null; relative_path: string; state: string; last_error: string | null; updated_at: string }

/** Drive filing: an AawazAI-owned folder in the firm's Drive/OneDrive that holds an organised copy of every document. */
export function MirrorPage() {
  const s = useSession();
  const qc = useQueryClient();
  const targets = useQuery({ queryKey: ["mirror"], queryFn: () => data<{ data: Target[] }>(portal.GET("/portal/v1/mirror-targets")) });
  const conns = useQuery({ queryKey: ["connections"], queryFn: () => data<{ data: Connection[] }>(portal.GET("/portal/v1/connections")) });
  const drives = (conns.data?.data ?? []).filter((c) => ["gdrive", "onedrive"].includes(c.connector_key) && c.status !== "revoked");
  const sinks = drives.filter((c) => c.config.write_enabled);
  const [creating, setCreating] = useState(false);
  const [problems, setProblems] = useState<Target | null>(null);
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "resync" | "pause" | "resume" }) => action === "resync"
      ? data(portal.POST("/portal/v1/mirror-targets/{target_id}:resync", { params: { path: { target_id: id } } }))
      : data(portal.POST("/portal/v1/mirror-targets/{target_id}:{action}", { params: { path: { target_id: id, action } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mirror"] }),
  });
  // one step for a connected drive: switches filing on and makes the AawazAI folder
  const start = useMutation({
    mutationFn: (id: string) => data(portal.POST("/portal/v1/connections/{connection_id}/filing:start", { params: { path: { connection_id: id } } })),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["mirror"] }), qc.invalidateQueries({ queryKey: ["connections"] }),
      qc.invalidateQueries({ queryKey: ["dashboard"] })]),
  });
  const kindOf = (id: string) => (conns.data?.data ?? []).find((c) => c.id === id)?.connector_key ?? "gdrive";
  const rows = targets.data?.data ?? [];
  const canCreate = s.can("enable_write") && sinks.length > 0;
  return (
    <>
      <div className="ui-explainer">
        <div className="ui-explainer-art" aria-hidden="true">
          <span><Icon name="message" size={18} /></span><Icon name="arrowRight" size={14} />
          <span><Icon name="sparkles" size={18} /></span><Icon name="arrowRight" size={14} />
          <span><Icon name="folder" size={18} /></span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong>Every document, filed into your own drive</strong>
          <p>AawazAI keeps an organised copy in a folder it owns: <span className="ui-mono">Client / FY / Month / Type</span>.
            Files you create or edit there are never overwritten, moved or deleted.</p>
        </div>
        {canCreate ? <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>New filing folder</Button> : null}
      </div>
      <ErrorBanner error={targets.error ?? act.error ?? start.error} />
      {targets.isLoading ? <Card><SkeletonRows rows={3} /></Card> : rows.length === 0 ? (
        <Card>
          <EmptyState icon="folder" title={drives.length ? "Your documents are not being filed into your drive yet" : "No drive connected"} action={
            drives.length && s.can("enable_write")
              ? <span className="ui-row" style={{ gap: 8 }}>
                  <Button variant="primary" icon="folder" busy={start.isPending} onClick={() => start.mutate((sinks[0] ?? drives[0])!.id)}>
                    Start filing into {(sinks[0] ?? drives[0])!.display_name}</Button>
                  {canCreate ? <Button onClick={() => setCreating(true)}>Choose the folder name or clients</Button> : null}
                </span>
              : drives.length ? undefined
              : <Link className="ui-btn ui-btn-primary" to="/channels">Connect Google Drive or OneDrive</Link>}>
            {!drives.length ? "Connect Google Drive or OneDrive. Every document your clients send is then filed there by client, year, month and type."
              : s.can("enable_write") ? "One click: AawazAI makes a folder called AawazAI in the drive and files every accepted document there by client, year, month and type, including the ones already received."
              : "Only the firm's owner can start filing into the drive. Ask them to open this page."}
          </EmptyState>
        </Card>
      ) : (
        <div className="ui-tiles">
          {rows.map((r) => {
            const running = r.backfill.state === "running";
            return (
              <div key={r.id} className="ui-card ui-tile">
                <div className="ui-tile-head">
                  <span className="ui-tile-logo"><ChannelLogo kind={kindOf(r.connection_id)} size={24} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <span className="ui-tile-eyebrow">{r.scope === "all_clients" ? "All clients" : `${r.client_org_ids?.length ?? 0} selected client${r.client_org_ids?.length === 1 ? "" : "s"}`}</span>
                    <strong className="ui-tile-name ui-ellipsis" title={r.root_display_path}>{r.root_display_path}</strong>
                  </div>
                  <Badge value={r.status} tone={r.status === "active" ? "green" : r.status === "paused" ? "amber" : undefined} />
                </div>
                {running ? <Meter value={(r.backfill.done ?? 0) / Math.max(1, r.backfill.total ?? 1)}
                                  label={`Copying earlier documents · ${r.backfill.done ?? 0} of ${r.backfill.total ?? "?"}`} />
                  : <p>{r.backfill.state === "done" ? "Up to date. New documents are copied as they are filed." : "New documents are copied as they are filed."}
                      {" "}Updated {fmtDate(r.updated_at)}.</p>}
                {r.problems ? (
                  <button className="ui-alert-row" onClick={() => setProblems(r)}>
                    <Icon name="alert" size={16} />{r.problems} file{r.problems === 1 ? "" : "s"} could not be copied<Icon name="chevron" size={14} />
                  </button>
                ) : null}
                {s.can("enable_write") ? (
                  <div className="ui-tile-foot">
                    <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: r.id, action: r.status === "paused" ? "resume" : "pause" })}
                            busy={act.isPending && act.variables?.id === r.id && act.variables.action !== "resync"}>{r.status === "paused" ? "Resume" : "Pause"}</Button>
                    <Button size="sm" icon="refresh" onClick={() => act.mutate({ id: r.id, action: "resync" })}
                            busy={act.isPending && act.variables?.id === r.id && act.variables.action === "resync"}>Check again</Button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      <NewTarget open={creating} onClose={() => setCreating(false)} sinks={sinks} />
      <Modal open={!!problems} size="lg" title="Files that could not be copied" icon={<Icon name="alert" size={20} />}
             subtitle={problems ? `In ${problems.root_display_path}. Each file says why. "Check again" on the folder looks at them once more.` : undefined}
             onClose={() => setProblems(null)}>
        {problems ? <Problems id={problems.id} onClose={() => setProblems(null)} /> : null}
      </Modal>
    </>
  );
}

function NewTarget({ open, onClose, sinks }: { open: boolean; onClose: () => void; sinks: Connection[] }) {
  const qc = useQueryClient();
  const clients = useClients();
  const [form, setForm] = useState({ connection_id: "", root_name: "AawazAI", scope: "all_clients" });
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const some = form.scope === "selected";
  const create = useMutation({
    mutationFn: () => data(portal.POST("/portal/v1/mirror-targets", { body: {
      connection_id: form.connection_id || sinks[0]?.id || "", root_name: form.root_name,
      scope: form.scope as "all_clients", client_org_ids: some ? [...chosen] : [] } })),
    onSuccess: async () => { onClose(); await qc.invalidateQueries({ queryKey: ["mirror"] }); },
  });
  return (
    <Modal open={open} size="sm" title="New filing folder" icon={<Icon name="folder" size={20} />}
           subtitle="AawazAI creates this folder and files documents inside it, for every client or only the ones you choose." onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <ErrorBanner error={create.error} />
        <Field label="Drive">{(id) => <Select id={id} value={form.connection_id || sinks[0]?.id || ""} onChange={(e) => setForm({ ...form, connection_id: e.target.value })}
          options={sinks.map((c) => ({ value: c.id, label: c.display_name }))} />}</Field>
        <Field label="Folder name" hint="Created at the top of the drive.">{(id) =>
          <Input id={id} required value={form.root_name} onChange={(e) => setForm({ ...form, root_name: e.target.value })} />}</Field>
        <Field label="Clients">{(id) => <Select id={id} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
          options={[{ value: "all_clients", label: "All clients, including ones added later" }, { value: "selected", label: "Only the clients I choose" }]} />}</Field>
        {some ? (
          <div className="ui-pick-list ui-pick-scroll" style={{ maxHeight: 180, marginBottom: 12 }}>
            {(clients.data?.data ?? []).map((c) => (
              <label key={c.id} className="ui-row"><input type="checkbox" checked={chosen.has(c.id)} onChange={() => {
                const n = new Set(chosen); n.has(c.id) ? n.delete(c.id) : n.add(c.id); setChosen(n);
              }} />{c.display_name}</label>
            ))}
            {(clients.data?.data.length ?? 0) === 0 ? <p className="ui-hint" style={{ margin: 0 }}>Add clients first.</p> : null}
          </div>
        ) : null}
        <div className="ui-folder-preview" aria-hidden="true">
          <span><Icon name="folder" size={14} />{form.root_name || "AawazAI"}</span>
          <span><Icon name="folder" size={14} />Ramesh Traders</span>
          <span><Icon name="folder" size={14} />FY 2026-27</span>
          <span><Icon name="folder" size={14} />2026-09 · Purchase invoices</span>
        </div>
        <p className="ui-hint" style={{ margin: 0 }}>Copies live in your own drive, outside AawazAI's India-region storage, at your direction. This is recorded.</p>
        <div className="ui-dialog-actions">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" busy={create.isPending} disabled={!form.root_name.trim() || (some && chosen.size === 0)}>Create folder</Button>
        </div>
      </form>
    </Modal>
  );
}

const PROBLEM: Record<string, string> = {
  conflict: "A file with this name was changed in the drive, so yours was left alone",
  detached: "The copy was moved or deleted in the drive",
  failed: "The drive refused the copy",
};

function Problems({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ["mirror", id, "problems"], queryFn: () => data<{ data: Entry[] }>(portal.GET(
    "/portal/v1/mirror-targets/{target_id}/entries", { params: { path: { target_id: id }, query: { state: "conflict,detached,failed" } } })) });
  const rows = q.data?.data ?? [];
  return (
    <>
      <ErrorBanner error={q.error} />
      {q.isLoading ? <SkeletonRows rows={3} /> : rows.length === 0 ? <p className="ui-hint" style={{ margin: 0 }}>Nothing left to resolve.</p> : (
        <div className="ui-pick-list ui-pick-scroll">
          {rows.map((r) => (
            <div key={r.id} className="ui-pick-row">
              <span className="ui-pick-icon"><Icon name="file" size={16} /></span>
              <div className="ui-pick-name"><span className="ui-mono" title={r.relative_path}>{r.relative_path}</span>
                <span className="ui-cell-sub">{PROBLEM[r.state] ?? "Not copied"}{r.last_error ? `: ${r.last_error}` : ""} · {fmtDate(r.updated_at)}</span></div>
            </div>
          ))}
        </div>
      )}
      <div className="ui-dialog-actions"><Button variant="primary" onClick={onClose}>Done</Button></div>
    </>
  );
}
